import React, {useMemo, useState} from 'react';
import CollegeService from '../../../services/CollegeService';
import {useDispatch} from 'react-redux';
import {changesSaved, errorMessage} from '../../../../shared/actions/notifications';
import {Form, Formik, FormikHelpers} from 'formik';
import FormikTextInput from '../../../components/inputs/formik-text-input';
import Label from '../../../../shared/components/label';
import Button from '../../../components/button';
import {College} from '../../../types/college';
import {buildApiError} from '../../../utils/errors';
import {FormFieldError} from '../../../utils/errors/FormFieldError';
import Yup from '../../../utils/yup';
import {useLocation} from 'react-router-dom';
import useAsyncEffect from '../../../hooks/use-async-effect';

type NewCollegeValues = Partial<College> & {cloneOfSlug?: string};

const schema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  nickname: Yup.string(),
  slug: Yup.string().test(
    'slug-format',
    'Slug may only contain letters, numbers, and hyphens',
    (value) => !value || /^[A-Za-z0-9-]+$/.test(value)
  ),
  unitId: Yup.string().test(
    'unitId-format',
    'Unit ID must be exactly 6 digits',
    (value) => !value || /^\d{6}$/.test(value)
  ),
  cloneOfSlug: Yup.string(),
});

const NewCollegeListing = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [initialValues, setInitialValues] = useState<NewCollegeValues>({});

  const cloneOfSlug = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('cloneOfSlug') || '';
  }, [location.search]);

  useAsyncEffect(async () => {
    if (!cloneOfSlug) {
      return;
    }

    const college = await CollegeService.show(cloneOfSlug, {fields: 'name,nickname,slug'});
    setInitialValues({
      name: `${college.name || ''} Clone`,
      nickname: college.nickname || undefined,
      slug: `${college.slug || ''}-clone`,
      cloneOfSlug,
    });
  }, [cloneOfSlug]);

  const saveCollege = async (
    values: NewCollegeValues,
    formikHelpers: FormikHelpers<NewCollegeValues>
  ) => {
    try {
      formikHelpers.setTouched({name: true, nickname: true, slug: true, unitId: true}, true);
      const {slug} = await CollegeService.create(values, {fields: 'slug'});
      dispatch(changesSaved());
      window.location.href = `/admin/college_listings/${slug}/edit`;
    } catch (errors) {
      const formFieldError = buildApiError(FormFieldError, errors) as FormFieldError;
      const formikErrors = formFieldError.getFieldErrors();
      formikHelpers.setErrors(formikErrors as Record<string, string>);
      dispatch(errorMessage());
    }
  };

  return (
    <Formik<NewCollegeValues>
      initialValues={initialValues}
      enableReinitialize
      validationSchema={schema}
      onSubmit={saveCollege}
      validateOnMount
    >
      {({isSubmitting, isValid}) => (
        <Form>
          <h1 className="shared-font headline">Create New College Listing</h1>
          <Label>
            College Name
            <FormikTextInput name="name" />
          </Label>
          <Label>
            Nickname
            <FormikTextInput name="nickname" />
          </Label>
          <Label>
            Slug
            <FormikTextInput name="slug" />
          </Label>
          {!cloneOfSlug && (
            <Label>
              Unit ID (IPEDS ID)
              <FormikTextInput name="unitId" />
            </Label>
          )}
          <Button type="submit" disabled={isSubmitting || !isValid}>
            {isSubmitting ? 'Submitting...' : 'Create new listing'}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default NewCollegeListing;
